"use client";

import { type FC, type Ref, type ReactNode, useMemo } from "react";
import {
  Button,
  type ButtonProps,
  Icon,
  type UIAction,
  useActions,
  type UserRecord,
  Avatar,
} from "@huub-web/ui";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";
import type { TemplateIcon } from "../../types/TemplateIcon";

export interface ButtonTemplateProps {
  value: string;
  action?: UIAction;
  variant?: "primary" | "secondary";
  type?: "previous" | "next" | "default" | "prompt" | "submit";
  icon?: TemplateIcon;
  user?: UserRecord;
  align?: "right";
}

export const ButtonTemplate: FC<
  ButtonTemplateProps & { ref?: Ref<HTMLButtonElement> }
> = ({
  value,
  action,
  icon,
  user,
  type = "default",
  variant = "primary",
  align,
  ref,
}) => {
  const { handleAction } = useActions();
  const { lockUI } = useTemplates();

  const contentItem = useMemo(
    () =>
      value && action && type !== "submit"
        ? { type: "cta" as const, label: value, action }
        : null,
    [value, action, type],
  );
  usePageContent("button", contentItem);

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  const buttonVariant: ButtonProps["variant"] = (() => {
    if (type === "prompt") {
      return "primary";
    }
    return variant === "primary" ? "primary" : "secondary";
  })();

  const leading: ReactNode = (() => {
    if (user) {
      return <Avatar id={user.id} value={user.name} size="xs" variant="user" />;
    }

    if (type === "previous") {
      return <Icon icon="chevronLeft" />;
    } else if (type === "prompt") {
      return <Icon icon="aiFilled" />;
    }

    return undefined;
  })();

  const trailing: ReactNode = (() => {
    if (icon) {
      return <Icon icon={icon} />;
    }
    if (type === "next") {
      return <Icon icon="chevronRight" />;
    }
    return undefined;
  })();

  return (
    <Button
      ref={ref}
      variant={buttonVariant}
      leading={leading}
      size="s"
      trailing={trailing}
      onClick={handleClick}
      disabled={lockUI}
      type={type === "submit" ? "submit" : "button"}
      alignSelf={align === "right" ? "flex-end" : undefined}
    >
      {value}
    </Button>
  );
};
